import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { AuthenticationService } from '../../features/authentication/services/authentication.service';
import { TranslationService } from '../translations/services/translation.service';

/**
 * PermissionGuard enforces role-based access at the routing layer.
 *
 * It must be chained AFTER AuthGuard (which verifies authentication).
 * Required roles are declared in route data:
 *   data: { roles: ['ROLE_A', 'ROLE_B'] }   // user needs AT LEAST ONE
 *
 * Roles are read directly from the JWT to prevent localStorage tampering.
 */
@Injectable({ providedIn: 'root' })
export class PermissionGuard implements CanActivate {
  private readonly authService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly translationService = inject(TranslationService);

  canActivate(route: ActivatedRouteSnapshot): boolean {
    const requiredRoles: string[] | undefined = route.data?.['roles'];

    // If no roles are declared for the route, allow access (authentication is enough)
    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }

    const userRoles = this.authService.getRolesFromToken();
    const hasRole = requiredRoles.some((role) => userRoles.includes(role));

    if (!hasRole) {
      this.router.navigate(['/access-denied'], {
        state: { reason: this.translationService.translate('accessDeniedMessageText') }
      });
      return false;
    }

    return true;
  }
}
